// src/utils/creditsApi.ts
//
// Wrapper around /api/credits and /api/admin/credits. Single source of
// truth for typed responses so the user-facing Credits page, the admin
// AdminCredits page, and the Apps list balance badge all agree on what
// the backend returns.

import API_BASE_URL from './apiBase';

// ---------------------------------------------------------------------------
// Shared shapes
// ---------------------------------------------------------------------------

/**
 * Bundle as returned from the public /packages endpoint AND the admin
 * GET /admin/credits/bundles endpoint. The `total` / `subtotal` /
 * `pricePerCredit` fields are computed server-side from the current
 * base price so the frontend never has to re-derive them.
 */
export interface CreditBundle {
  id: number;
  name: string;
  credits: number;
  discountPercent: number;
  isActive: boolean;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;

  // Computed pricing (only present on /packages or when the server
  // applied base_price_per_credit — admin CRUD omits these fields).
  percentOff?: number;
  pricePerCredit?: number;
  subtotal?: number;
  total?: number;
  savings?: number;
  packageId?: string; // e.g. `bundle_12`
}

export interface CreditQuote {
  credits: number;
  percentOff: number;
  pricePerCredit: number;
  subtotal: number;
  total: number;
  savings: number;
  promoDiscount?: number;
  totalAfterPromo?: number;
}

export interface CreditTransaction {
  id: number;
  delta: number;
  balance_after: number;
  kind: 'purchase' | 'usage' | 'grant' | 'refund' | 'adjustment';
  reference_type: string | null;
  reference_id: string | null;
  note: string | null;
  created_at: string;
}

export interface ActiveClock {
  id: number;
  target_type: 'app' | 'game';
  target_id: string;
  target_name: string | null;
  credits_charged: number;
  period: 'day' | 'week' | 'month' | 'year';
  charged_at: string;
  expires_at: string;
}

export interface PromoCode {
  id: number;
  code: string;
  description: string | null;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  bundleId: number | null;
  maxUses: number | null;
  uses: number;
  validFrom: string | null;
  validUntil: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface PromoValidation {
  discount: number;
  promo: {
    id: number;
    code: string;
    description: string | null;
    discountType: 'percent' | 'fixed';
    discountValue: number;
    bundleId: number | null;
  };
  subtotal: number;
  total: number;
}

// Subscription tiers

export interface SubscriptionTier {
  tier: string;
  name: string;
  monthlyCredits: number;
  pricePerMonth: number;
  description: string;
  features: string[];
  roleRequired: string | null;
}

export interface UserSubscription {
  tier: string;
  name: string;
  monthlyCredits: number;
  pricePerMonth: number;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  lastRefillAt: string | null;
  nextRefillAt: string | null;
  description: string;
  features: string[];
}

export interface AppCreditTier {
  id: number;
  period: string;
  credit_cost: number;
  sort_order: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const withAuth = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

// ---------------------------------------------------------------------------
// Public / user endpoints
// ---------------------------------------------------------------------------

export const fetchCreditBalance = async (token: string): Promise<number> => {
  try {
    const res = await fetch(`${API_BASE_URL}/credits/balance`, { headers: withAuth(token) });
    if (!res.ok) return 0;
    const json = await res.json();
    return Number(json.balance ?? 0);
  } catch (err) {
    console.error('Failed to fetch credit balance:', err);
    return 0;
  }
};

export const fetchCreditPackages = async (): Promise<{
  packages: CreditBundle[];
  basePricePerCredit: number;
}> => {
  const res = await fetch(`${API_BASE_URL}/credits/packages`);
  if (!res.ok) throw new Error('Failed to load credit packages');
  return await res.json();
};

export const fetchCustomQuote = async (
  credits: number,
  promoCode?: string,
): Promise<CreditQuote | null> => {
  try {
    const qs = new URLSearchParams({ credits: String(credits) });
    if (promoCode) qs.set('promo', promoCode);
    const res = await fetch(`${API_BASE_URL}/credits/quote?${qs.toString()}`);
    if (!res.ok) return null;
    const json = await res.json();
    return json.quote;
  } catch {
    return null;
  }
};

/**
 * Validate a promo code against a specific bundle (or no bundle for
 * custom purchases). Returns the server-computed discount + totals,
 * or null if the code was rejected — the caller reads `res.status` for
 * the reason message when needed.
 */
export const validatePromo = async (
  token: string,
  body: { code: string; bundleId?: number | null; subtotal?: number },
): Promise<{ ok: true; data: PromoValidation } | { ok: false; error: string }> => {
  try {
    const res = await fetch(`${API_BASE_URL}/credits/validate-promo`, {
      method: 'POST',
      headers: withAuth(token),
      body: JSON.stringify(body),
    });
    const json = await res.json();
    if (!res.ok || !json.success) {
      return { ok: false, error: json.error || 'Invalid promo code' };
    }
    return { ok: true, data: json };
  } catch (err) {
    console.error('Promo validation failed:', err);
    return { ok: false, error: 'Failed to validate promo code' };
  }
};

/**
 * Start a credit-pack purchase. Paid packs return a Stripe Checkout `url` to
 * redirect to; genuinely $0 packs (free/role bundles, 100%-off promos) are
 * granted directly and return `{ free, balance }`.
 */
export const purchaseCredits = async (
  token: string,
  body: { bundleId?: number; promoCode?: string },
): Promise<{ url?: string; free?: boolean; balance?: number; credited?: number; message?: string } | null> => {
  try {
    const res = await fetch(`${API_BASE_URL}/stripe/create-topup-checkout`, {
      method: 'POST',
      headers: withAuth(token),
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.url) return { url: json.url };

    // $0 packs can't go through Stripe — grant directly.
    const free = await fetch(`${API_BASE_URL}/credits/purchase`, {
      method: 'POST',
      headers: withAuth(token),
      body: JSON.stringify(body),
    });
    if (!free.ok) return null;
    const fj = await free.json();
    return { free: true, balance: fj.balance, credited: fj.credited, message: fj.message };
  } catch (err) {
    console.error('Credit purchase failed:', err);
    return null;
  }
};

/** Confirm a returned Checkout session (grants immediately if the webhook lagged). */
export const confirmCheckoutSession = async (
  token: string,
  sessionId: string,
): Promise<{ balance: number } | null> => {
  try {
    const res = await fetch(`${API_BASE_URL}/stripe/confirm-session`, {
      method: 'POST',
      headers: withAuth(token),
      body: JSON.stringify({ sessionId }),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

export const fetchCreditHistory = async (token: string): Promise<CreditTransaction[]> => {
  try {
    const res = await fetch(`${API_BASE_URL}/credits/history`, { headers: withAuth(token) });
    if (!res.ok) return [];
    const json = await res.json();
    return json.transactions ?? [];
  } catch (err) {
    console.error('Failed to fetch credit history:', err);
    return [];
  }
};

export const fetchActiveClocks = async (token: string): Promise<ActiveClock[]> => {
  try {
    const res = await fetch(`${API_BASE_URL}/credits/clocks`, { headers: withAuth(token) });
    if (!res.ok) return [];
    const json = await res.json();
    return json.clocks ?? [];
  } catch (err) {
    console.error('Failed to fetch active clocks:', err);
    return [];
  }
};

// ---------------------------------------------------------------------------
// Admin endpoints
// ---------------------------------------------------------------------------

export const fetchAdminPricing = async (token: string): Promise<{ basePricePerCredit: number }> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/pricing`, { headers: withAuth(token) });
  if (!res.ok) throw new Error('Failed to load pricing');
  const json = await res.json();
  return { basePricePerCredit: Number(json.settings?.base_price_per_credit ?? 0) };
};

export const updateAdminPricing = async (
  token: string,
  basePricePerCredit: number,
): Promise<boolean> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/pricing`, {
    method: 'PUT',
    headers: withAuth(token),
    body: JSON.stringify({ basePricePerCredit }),
  });
  return res.ok;
};

export const fetchAdminBundles = async (token: string): Promise<CreditBundle[]> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/bundles`, { headers: withAuth(token) });
  if (!res.ok) return [];
  const json = await res.json();
  return json.bundles ?? [];
};

export const createAdminBundle = async (
  token: string,
  body: { name: string; credits: number; discountPercent: number; isActive: boolean; sortOrder: number },
): Promise<CreditBundle | null> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/bundles`, {
    method: 'POST',
    headers: withAuth(token),
    body: JSON.stringify(body),
  });
  if (!res.ok) return null;
  const json = await res.json();
  return json.bundle ?? null;
};

export const updateAdminBundle = async (
  token: string,
  id: number,
  patch: Partial<{ name: string; credits: number; discountPercent: number; isActive: boolean; sortOrder: number }>,
): Promise<CreditBundle | null> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/bundles/${id}`, {
    method: 'PUT',
    headers: withAuth(token),
    body: JSON.stringify(patch),
  });
  if (!res.ok) return null;
  const json = await res.json();
  return json.bundle ?? null;
};

export const deleteAdminBundle = async (token: string, id: number): Promise<boolean> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/bundles/${id}`, {
    method: 'DELETE',
    headers: withAuth(token),
  });
  return res.ok;
};

export const fetchAdminPromoCodes = async (token: string): Promise<PromoCode[]> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/promo-codes`, { headers: withAuth(token) });
  if (!res.ok) return [];
  const json = await res.json();
  return json.promoCodes ?? [];
};

export const createAdminPromoCode = async (
  token: string,
  body: Partial<PromoCode> & { code: string; discountType: 'percent' | 'fixed'; discountValue: number },
): Promise<PromoCode | null> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/promo-codes`, {
    method: 'POST',
    headers: withAuth(token),
    body: JSON.stringify(body),
  });
  if (!res.ok) return null;
  const json = await res.json();
  return json.promoCode ?? null;
};

export const updateAdminPromoCode = async (
  token: string,
  id: number,
  patch: Partial<PromoCode>,
): Promise<PromoCode | null> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/promo-codes/${id}`, {
    method: 'PUT',
    headers: withAuth(token),
    body: JSON.stringify(patch),
  });
  if (!res.ok) return null;
  const json = await res.json();
  return json.promoCode ?? null;
};

export const deleteAdminPromoCode = async (token: string, id: number): Promise<boolean> => {
  const res = await fetch(`${API_BASE_URL}/admin/credits/promo-codes/${id}`, {
    method: 'DELETE',
    headers: withAuth(token),
  });
  return res.ok;
};

// ---------------------------------------------------------------------------
// Subscription tiers
// ---------------------------------------------------------------------------

export const fetchSubscriptionTiers = async (token?: string | null): Promise<SubscriptionTier[]> => {
  try {
    const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(`${API_BASE_URL}/credits/subscription-tiers`, { headers });
    if (!res.ok) return [];
    const json = await res.json();
    return json.tiers ?? [];
  } catch { return []; }
};

export const fetchMySubscription = async (token: string): Promise<UserSubscription | null> => {
  try {
    const res = await fetch(`${API_BASE_URL}/credits/my-subscription`, { headers: withAuth(token) });
    if (!res.ok) return null;
    const json = await res.json();
    return json.subscription ?? null;
  } catch { return null; }
};

/**
 * Start a subscription. Paid tiers return a Stripe Checkout `url` (mode:
 * subscription); free tiers (admin/beta) activate directly and return `{ free }`.
 */
export const subscribe = async (
  token: string,
  tier: string,
): Promise<{ url?: string; free?: boolean; success?: boolean; message?: string } | null> => {
  try {
    const res = await fetch(`${API_BASE_URL}/stripe/create-subscription-checkout`, {
      method: 'POST',
      headers: withAuth(token),
      body: JSON.stringify({ tier }),
    });
    const json = await res.json().catch(() => ({}));
    if (res.ok && json.url) return { url: json.url };

    // Free tiers don't go through Stripe — activate directly.
    const free = await fetch(`${API_BASE_URL}/credits/subscribe`, {
      method: 'POST',
      headers: withAuth(token),
      body: JSON.stringify({ tier }),
    });
    if (!free.ok) return null;
    const fj = await free.json();
    return { free: true, success: fj.success, message: fj.message };
  } catch { return null; }
};

export const fetchAppCreditTiers = async (type: string, id: string): Promise<AppCreditTier[]> => {
  try {
    const res = await fetch(`${API_BASE_URL}/credits/app-tiers/${type}/${id}`);
    if (!res.ok) return [];
    const json = await res.json();
    return json.tiers ?? [];
  } catch { return []; }
};

// ---------------------------------------------------------------------------
// Admin: grant credits to another user
// ---------------------------------------------------------------------------

export const adminGrantCredits = async (
  token: string,
  body: { userId: number; credits: number; note?: string },
): Promise<{ success: boolean; balance?: number; credited?: number; user?: { id: number; name: string; email: string } } | null> => {
  try {
    const res = await fetch(`${API_BASE_URL}/admin/credits/grant-credits`, {
      method: 'POST',
      headers: withAuth(token),
      body: JSON.stringify(body),
    });
    return await res.json();
  } catch { return null; }
};

// ---------------------------------------------------------------------------
// Purchase availability (Stripe configured?) + admin payment settings
// ---------------------------------------------------------------------------
export const fetchPurchaseStatus = async (): Promise<{ available: boolean }> => {
  try {
    const res = await fetch(`${API_BASE_URL}/credits/purchase-status`);
    if (!res.ok) return { available: false };
    return await res.json();
  } catch {
    return { available: false };
  }
};

export interface PaymentSettings {
  available: boolean;
  publishableKey: string;
  secretKeySet: boolean;
  secretKeyMasked: string;
  webhookSecretSet: boolean;
  webhookSecretMasked: string;
}

export const fetchPaymentSettings = async (token: string): Promise<PaymentSettings | null> => {
  try {
    const res = await fetch(`${API_BASE_URL}/admin/payment-settings`, { headers: withAuth(token) });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

export const savePaymentSettings = async (
  token: string,
  body: { stripeSecretKey?: string; stripePublishableKey?: string; stripeWebhookSecret?: string },
): Promise<PaymentSettings | null> => {
  try {
    const res = await fetch(`${API_BASE_URL}/admin/payment-settings`, {
      method: 'PUT',
      headers: withAuth(token),
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};
